import { useMemo, useState } from 'react';

import { Box, Menu, MenuItem } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/system';

import ActionButtonAlignment from '~components/buttons/actionButtonAlignment';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import VectorImage from '~components/media/vectorImage';
import Dimens from '~themes/dimens';

import type { ActionBarAction } from './actionBarAction';
import { useActionBarContext } from './useActionBar';
import ActionButton from '../buttons/actionButton';

const ActionBar = () => {
	const { actions, overflowLimit } = useActionBarContext();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const theme = useTheme();
	const forceCollapse = useMediaQuery(theme.breakpoints.down('sm'));

	const { visibleActions, overflowActions } = useMemo(() => {
		return {
			visibleActions: actions.slice(0, forceCollapse ? 0 : overflowLimit),
			overflowActions: actions.slice(forceCollapse ? 0 : overflowLimit),
		};
	}, [actions, forceCollapse, overflowLimit]);

	if (actions.length === 0) {
		return null;
	}

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = (action: ActionBarAction) => {
		action.onClick?.();
		handleMenuClose();
	};

	return (
		<Box
			sx={{
				display: 'flex',
				gap: Dimens.spacingLg,
				alignItems: 'center',
			}}
		>
			{visibleActions.map((action) => (
				<ActionButton
					key={action.id}
					label={action.label}
					startIcon={action.icon}
					style={action.style}
					disabled={action.disabled}
					loading={action.loading}
					onClick={action.onClick}
					variant={action.variant}
				/>
			))}
			{overflowActions.length > 0 && (
				<>
					<ActionButton
						startIcon={<VectorImage name='icon/dots-vertical' />}
						onClick={handleMenuOpen}
					/>
					<Menu
						sx={{ marginTop: Dimens.spacingMd }}
						id='action-bar-overflow-menu'
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleMenuClose}
						slotProps={{
							paper: {
								sx: {
									borderRadius: Dimens.radiusMd,
									boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16)',
									overflow: 'hidden',
								},
							},
							list: {
								sx: { gap: 0, alignItems: 'stretch', p: Dimens.spacingSm },
							},
						}}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
						keepMounted
					>
						{overflowActions.map((action) => {
							return (
								<MenuItem
									key={action.id}
									onClick={() => handleMenuItemClick(action)}
									disabled={action.disabled}
									sx={{
										'&.MuiMenuItem-root:hover': {
											backgroundColor: 'transparent',
										},
										'& .MuiMenuItem-root:hover': {
											backgroundColor: 'transparent',
										},
										margin: 0,
										padding: 0,
									}}
								>
									<ActionButton
										sx={{ width: '100%' }}
										label={action.label}
										variant={action.variant}
										style={ActionButtonStyle.Text}
										startIcon={action.icon}
										alignment={ActionButtonAlignment.Start}
									/>
								</MenuItem>
							);
						})}
					</Menu>
				</>
			)}
		</Box>
	);
};

export default ActionBar;
